// ── Per-chain send/broadcast — signs with an in-memory key, talks directly to public
// RPCs (never through our own backend), exactly like every other self-custody wallet. ──
import { ethers } from 'ethers';
import { Connection, Keypair, PublicKey, SystemProgram, Transaction, VersionedTransaction } from '@solana/web3.js';
import * as spl from '@solana/spl-token';
import * as bitcoin from 'bitcoinjs-lib';
import * as ecc from '@bitcoinerlab/secp256k1';
import ECPairFactory from 'ecpair';
import bs58 from 'bs58';
import { secp256k1 } from '@noble/curves/secp256k1';
import { sha256 } from '@noble/hashes/sha256';
import { EVM_CHAINS } from './chains.js';

bitcoin.initEccLib(ecc);
const ECPair = ECPairFactory(ecc);

// Exact decimal-string -> integer conversion (no float rounding), for callers outside this module.
export function parseUnits(amount, decimals) { return ethers.parseUnits(String(amount), decimals); }

// ── EVM ──────────────────────────────────────────────────────────────────────
const _evmProviders = {};
export async function getEvmProvider(chain) {
  if (_evmProviders[chain]) return _evmProviders[chain];
  const cfg = EVM_CHAINS[chain];
  for (const url of cfg.rpcs) {
    try {
      const net = ethers.Network.from(cfg.chainId);
      const p = new ethers.JsonRpcProvider(url, net, { staticNetwork: net, batchMaxCount: 1 });
      await p.getBlockNumber();
      _evmProviders[chain] = p;
      return p;
    } catch {}
  }
  throw new Error(`Could not reach any RPC for ${chain}.`);
}

export async function sendEvm({ chain, privateKey, to, amount, contract, decimals }) {
  const provider = await getEvmProvider(chain);
  const wallet = new ethers.Wallet(privateKey, provider);
  const feeData = await provider.getFeeData();
  const gasFields = feeData.maxFeePerGas
    ? { maxFeePerGas: feeData.maxFeePerGas, maxPriorityFeePerGas: feeData.maxPriorityFeePerGas }
    : { gasPrice: feeData.gasPrice };

  if (!contract) {
    const value = ethers.parseUnits(amount, decimals ?? 18);
    const tx = await wallet.sendTransaction({ to, value, ...gasFields });
    return { hash: tx.hash };
  }
  const iface = new ethers.Interface(['function transfer(address,uint256) returns (bool)']);
  const value = ethers.parseUnits(amount, decimals);
  const data = iface.encodeFunctionData('transfer', [to, value]);
  const tx = await wallet.sendTransaction({ to: contract, data, ...gasFields });
  return { hash: tx.hash };
}

export async function estimateEvmFee({ chain, from, to, amount, contract, decimals }) {
  const provider = await getEvmProvider(chain);
  const feeData = await provider.getFeeData();
  const gasPrice = feeData.maxFeePerGas || feeData.gasPrice;
  let gasLimit;
  try {
    if (!contract) {
      gasLimit = await provider.estimateGas({ from, to, value: ethers.parseUnits(amount || '0', decimals ?? 18) });
    } else {
      const iface = new ethers.Interface(['function transfer(address,uint256) returns (bool)']);
      const data = iface.encodeFunctionData('transfer', [to || ethers.ZeroAddress, ethers.parseUnits(amount || '0', decimals)]);
      gasLimit = await provider.estimateGas({ from, to: contract, data });
    }
  } catch { gasLimit = contract ? 65000n : 21000n; }
  const feeWei = gasPrice * gasLimit;
  return { feeNative: ethers.formatEther(feeWei), gasLimit: gasLimit.toString() };
}

export async function signAndSendEvmSwapTx({ chain, privateKey, to, data, value }) {
  const provider = await getEvmProvider(chain);
  const wallet = new ethers.Wallet(privateKey, provider);
  const feeData = await provider.getFeeData();
  const gasFields = feeData.maxFeePerGas ? { maxFeePerGas: feeData.maxFeePerGas, maxPriorityFeePerGas: feeData.maxPriorityFeePerGas } : { gasPrice: feeData.gasPrice };
  const tx = await wallet.sendTransaction({ to, data, value: value ? BigInt(value) : 0n, ...gasFields });
  return { hash: tx.hash };
}

export async function approveErc20IfNeeded({ chain, privateKey, owner, token, spender, amount }) {
  const provider = await getEvmProvider(chain);
  const wallet = new ethers.Wallet(privateKey, provider);
  const iface = new ethers.Interface(['function allowance(address,address) view returns (uint256)', 'function approve(address,uint256) returns (bool)']);
  const c = new ethers.Contract(token, iface, provider);
  const current = await c.allowance(owner, spender);
  if (current >= amount) return null;
  const feeData = await provider.getFeeData();
  const gasFields = feeData.maxFeePerGas ? { maxFeePerGas: feeData.maxFeePerGas, maxPriorityFeePerGas: feeData.maxPriorityFeePerGas } : { gasPrice: feeData.gasPrice };
  const data = iface.encodeFunctionData('approve', [spender, ethers.MaxUint256]);
  const tx = await wallet.sendTransaction({ to: token, data, ...gasFields });
  await tx.wait();
  return tx.hash;
}

// ── Solana ───────────────────────────────────────────────────────────────────
const SOL_RPCS = ['https://api.mainnet-beta.solana.com', 'https://solana-rpc.publicnode.com'];
async function getSolConnection() {
  for (const rpc of SOL_RPCS) {
    try { const c = new Connection(rpc, 'confirmed'); await c.getLatestBlockhash(); return c; } catch {}
  }
  throw new Error('Could not reach any Solana RPC.');
}

function solKeypairFromSecret(secretKeyBase58) {
  return Keypair.fromSecretKey(bs58.decode(secretKeyBase58));
}

function sleep(ms) { return new Promise(r => setTimeout(r, ms)); }

// `sendAndConfirmTransaction` throws "Signature has expired: block height exceeded" whenever its
// own polling loop times out — which happens even when the transaction actually landed a moment
// later (confirmed empirically: a real send here reported "failed" while the recipient's balance
// had already updated on-chain). Resending the same signed tx is safe — Solana dedupes by
// signature — so keep resending until the blockhash truly expires, checking status after every
// attempt, and only report failure once the network confirms it, not just because a client-side
// timer fired.
async function robustSendAndConfirm(conn, tx, signers) {
  tx.feePayer = signers[0].publicKey;
  const { blockhash, lastValidBlockHeight } = await conn.getLatestBlockhash('confirmed');
  tx.recentBlockhash = blockhash;
  tx.sign(...signers);
  const rawTx = tx.serialize();
  const signature = bs58.encode(tx.signature);

  const checkStatus = async () => {
    const { value } = await conn.getSignatureStatuses([signature], { searchTransactionHistory: true });
    return value[0];
  };

  const deadline = Date.now() + 90_000;
  let lastSendErr = null;
  while (Date.now() < deadline) {
    try { await conn.sendRawTransaction(rawTx, { skipPreflight: false, maxRetries: 0 }); }
    catch (e) { lastSendErr = e; }

    const status = await checkStatus().catch(() => null);
    if (status) {
      if (status.err) throw new Error('Transaction failed on-chain: ' + JSON.stringify(status.err));
      if (status.confirmationStatus === 'confirmed' || status.confirmationStatus === 'finalized') return signature;
    }
    const height = await conn.getBlockHeight('confirmed').catch(() => 0);
    if (height > lastValidBlockHeight) break; // blockhash window has truly closed
    await sleep(1500);
  }

  const finalStatus = await checkStatus().catch(() => null);
  if (finalStatus && !finalStatus.err) return signature;
  throw lastSendErr || new Error('Transaction was not confirmed before its blockhash expired. It may not have been sent — check the explorer before retrying.');
}

export async function sendSolana({ secretKey, to, amount, contract, decimals }) {
  const conn = await getSolConnection();
  const kp = solKeypairFromSecret(secretKey);
  const toPub = new PublicKey(to);

  if (!contract) {
    const lamports = BigInt(ethers.parseUnits(amount, 9));
    const tx = new Transaction().add(SystemProgram.transfer({ fromPubkey: kp.publicKey, toPubkey: toPub, lamports }));
    const sig = await robustSendAndConfirm(conn, tx, [kp]);
    return { hash: sig };
  }
  const mint = new PublicKey(contract);
  const fromAta = await spl.getAssociatedTokenAddress(mint, kp.publicKey);
  const toAta = await spl.getAssociatedTokenAddress(mint, toPub);
  const tx = new Transaction();
  if (!(await conn.getAccountInfo(toAta))) tx.add(spl.createAssociatedTokenAccountInstruction(kp.publicKey, toAta, toPub, mint));
  const raw = BigInt(ethers.parseUnits(amount, decimals));
  tx.add(spl.createTransferInstruction(fromAta, toAta, kp.publicKey, raw));
  const sig = await robustSendAndConfirm(conn, tx, [kp]);
  return { hash: sig };
}

export async function estimateSolanaFee() {
  return { feeNative: '0.000005' }; // Solana's base fee is fixed per signature
}

export async function signAndSendSolanaSwapTx({ secretKey, swapTransactionB64 }) {
  const conn = await getSolConnection();
  const kp = solKeypairFromSecret(secretKey);
  const tx = VersionedTransaction.deserialize(Buffer.from(swapTransactionB64, 'base64'));
  tx.sign([kp]);
  const rawTx = tx.serialize();
  const signature = bs58.encode(tx.signatures[0]);

  // Same false-failure risk as robustSendAndConfirm above (confirmTransaction can throw on its own
  // timeout even after the tx lands) — poll status directly instead of trusting one confirm call.
  const { lastValidBlockHeight } = await conn.getLatestBlockhash('confirmed');
  const deadline = Date.now() + 90_000;
  let lastSendErr = null;
  while (Date.now() < deadline) {
    try { await conn.sendRawTransaction(rawTx, { skipPreflight: false, maxRetries: 0 }); }
    catch (e) { lastSendErr = e; }
    const { value } = await conn.getSignatureStatuses([signature], { searchTransactionHistory: true }).catch(() => ({ value: [null] }));
    const status = value[0];
    if (status) {
      if (status.err) throw new Error('Swap failed on-chain: ' + JSON.stringify(status.err));
      if (status.confirmationStatus === 'confirmed' || status.confirmationStatus === 'finalized') return { hash: signature };
    }
    const height = await conn.getBlockHeight('confirmed').catch(() => 0);
    if (height > lastValidBlockHeight) break;
    await sleep(1500);
  }
  const { value } = await conn.getSignatureStatuses([signature], { searchTransactionHistory: true }).catch(() => ({ value: [null] }));
  if (value[0] && !value[0].err) return { hash: signature };
  throw lastSendErr || new Error('Swap was not confirmed before its blockhash expired. Check the explorer before retrying.');
}

// ── Bitcoin ──────────────────────────────────────────────────────────────────
async function btcFeeRate() {
  try {
    const d = await fetch('https://blockstream.info/api/fee-estimates').then(r => r.json());
    return Math.max(1, Math.ceil(d['3'] || d['6'] || 5));
  } catch { return 5; }
}

export async function estimateBtcFee({ from, amount }) {
  const utxos = await fetch(`https://blockstream.info/api/address/${from}/utxo`).then(r => r.json());
  const rate = await btcFeeRate();
  const vbytes = 10.5 + utxos.length * 68 + 2 * 31; // rough native-segwit estimate
  return { feeNative: (Math.ceil(vbytes * rate) / 1e8).toFixed(8) };
}

export async function sendBitcoin({ privateKeyWIF, from, to, amount }) {
  const network = bitcoin.networks.bitcoin;
  const keyPair = ECPair.fromWIF(privateKeyWIF, network);
  const utxos = await fetch(`https://blockstream.info/api/address/${from}/utxo`).then(r => r.json());
  if (!utxos.length) throw new Error('No spendable UTXOs found for this address.');
  const rate = await btcFeeRate();

  const payment = bitcoin.payments.p2wpkh({ pubkey: Buffer.from(keyPair.publicKey), network });
  const psbt = new bitcoin.Psbt({ network });
  let inputSum = 0;
  for (const u of utxos) {
    psbt.addInput({ hash: u.txid, index: u.vout, witnessUtxo: { value: u.value, script: payment.output } });
    inputSum += u.value;
  }
  const sendSats = Math.round(parseFloat(amount) * 1e8);
  const vbytes = 10.5 + utxos.length * 68 + 2 * 31;
  const fee = Math.ceil(vbytes * rate);
  const change = inputSum - sendSats - fee;
  if (change < 0) throw new Error('Insufficient BTC balance to cover amount + fee.');

  psbt.addOutput({ address: to, value: sendSats });
  if (change > 546) psbt.addOutput({ address: from, value: change });

  const signer = { publicKey: Buffer.from(keyPair.publicKey), sign: (hash) => Buffer.from(keyPair.sign(hash)) };
  psbt.signAllInputs(signer);
  psbt.finalizeAllInputs();
  const txHex = psbt.extractTransaction().toHex();
  const txid = await fetch('https://blockstream.info/api/tx', { method: 'POST', body: txHex }).then(async r => {
    const t = await r.text();
    if (!r.ok) throw new Error(t);
    return t;
  });
  return { hash: txid };
}

// ── Tron ─────────────────────────────────────────────────────────────────────
function tronSign(txID, privateKeyHex) {
  const priv = Uint8Array.from(Buffer.from(privateKeyHex, 'hex'));
  const sig = secp256k1.sign(txID, priv, { lowS: false });
  const r = sig.r.toString(16).padStart(64, '0');
  const s = sig.s.toString(16).padStart(64, '0');
  const v = sig.recovery.toString(16).padStart(2, '0');
  return r + s + v;
}

export async function estimateTronFee({ contract }) {
  return { feeNative: contract ? '15' : '1.1' }; // rough TRX reserve — real fee depends on bandwidth/energy state
}

export async function sendTron({ privateKey, from, to, amount, contract, decimals }) {
  if (!contract) {
    const sun = Math.round(parseFloat(amount) * 1e6);
    const built = await fetch('https://api.trongrid.io/wallet/createtransaction', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ to_address: to, owner_address: from, amount: sun, visible: true }),
    }).then(r => r.json());
    if (built.Error) throw new Error(built.Error);
    const signature = tronSign(Uint8Array.from(Buffer.from(built.txID, 'hex')), privateKey);
    const result = await fetch('https://api.trongrid.io/wallet/broadcasttransaction', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...built, signature: [signature] }),
    }).then(r => r.json());
    if (!result.result) throw new Error(result.message ? Buffer.from(result.message, 'hex').toString() : 'Broadcast failed.');
    return { hash: built.txID };
  }
  // TRC-20 transfer via triggersmartcontract
  const raw = BigInt(ethers.parseUnits(amount, decimals));
  const built = await fetch('https://api.trongrid.io/wallet/triggersmartcontract', {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      owner_address: from, contract_address: contract, function_selector: 'transfer(address,uint256)',
      parameter: encodeTronParams(to, raw), fee_limit: 100_000_000, call_value: 0, visible: true,
    }),
  }).then(r => r.json());
  if (built.result?.result === false) throw new Error(Buffer.from(built.result.message || '', 'hex').toString() || 'Contract call rejected.');
  const tx = built.transaction;
  const signature = tronSign(Uint8Array.from(Buffer.from(tx.txID, 'hex')), privateKey);
  const result = await fetch('https://api.trongrid.io/wallet/broadcasttransaction', {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ...tx, signature: [signature] }),
  }).then(r => r.json());
  if (!result.result) throw new Error(result.message ? Buffer.from(result.message, 'hex').toString() : 'Broadcast failed.');
  return { hash: tx.txID };
}

function encodeTronParams(toAddress, amount) {
  // ABI-encode (address, uint256) for triggersmartcontract's hex `parameter` field.
  const addrHex = tronBase58ToHex(toAddress).slice(2).padStart(64, '0'); // drop 0x41 prefix, left-pad to 32 bytes
  const amtHex = amount.toString(16).padStart(64, '0');
  return addrHex + amtHex;
}
function tronBase58ToHex(address) {
  const bytes = bs58.decode(address).slice(0, -4); // strip 4-byte checksum
  return '0x' + Buffer.from(bytes.slice(1)).toString('hex'); // drop the 0x41 version byte for the padded param
}
